import { AdUnitFormat } from "@store";

/**

Valid combinations are

rate(impression,click)
rate(impression,view)

rate(click,landing)
rate(click,passbook_fetch)

rate(landing,session)

rate(session,signup)
rate(session,order)

rate(passbook_fetch,passbook_install)

rate(impression,inter0)
rate(impression,inter1)
rate(impression,inter2)
rate(impression,inter3)
rate(impression,custom0)
rate(impression,custom1)
rate(impression,custom2)
rate(impression,custom3)
rate(impression,interopen)
rate(impression,interclose)
rate(impression interaction)

*/

export const KnownMetricCodes = {
    impression: "impression",
    view: "view",
    impressionViewRate: "rate(impression,view)",
    viewBin6: "bin(view,6)",
    click: "click",
    clickBin6: "bin(click,6)",
    landing: "landing",
    impressionClickRate: "rate(impression,click)",
    viewClickRate: "rate(view,click)",
    clickLandingRate: "rate(click,landing)",
    landingSessionRate: "rate(landing,session)",
    interaction: "interaction"
};

export const KnownDimensionCodes = {
    date: "date",
    environment: "environment",
    weekday: "weekday",
    agegroup: "agegroup",
    gender: "gender",
    os: "os",
    region: "region",
    adFormat: "adformat",
    adTemplate: "adtemplate"
};

export function campaignMetrics(campaignUnit: string): MetricInfo[] {
    return MetricUnitMap[campaignUnit];
}

export function adInteractionActionsMetrics(adUnits: AdUnitFormat[]): string[] {
    const interactions = new Set<string>();

    adUnits.forEach(adu => {
        adu.types.forEach(adt => {
            adt.interaction.actions.forEach(a => {
                interactions.add(a.type);
            });
        });
    });

    return [
        ...AdInteractionMetrics.map(m => m.metric),
        ...Array.from(interactions.values()).map(i => `rate(${KnownMetricCodes.impression},${i})`)
    ];
}

export function makeBinMetric(metric: string, binNumber: number): string {
    return `bin(${metric},${binNumber})`;
}

export function isBinMetric(metric: string): boolean {
    return metric.indexOf("bin(") === 0;
}

export interface MetricInfo {
    metric: string;
    isRate: boolean;
    color: string;
    rateMetric?: string;
    showInTable: boolean;
    showInLegend: boolean;
    isDefault: boolean;
}

const MetricUnitMap: { [unit: string]: MetricInfo[] } = {
    CPM: [
        {
            metric: KnownMetricCodes.impression,
            isRate: false,
            color: "#6FBEA2",
            showInLegend: true,
            showInTable: true,
            isDefault: true
        },
        {
            metric: KnownMetricCodes.impressionClickRate,
            isRate: true,
            color: "#6FBEA2",
            showInLegend: false,
            showInTable: true,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.click,
            isRate: false,
            color: "#324472",
            showInLegend: true,
            showInTable: false,
            isDefault: false,
            rateMetric: KnownMetricCodes.impressionClickRate
        }
    ],
    vCPM: [
        {
            metric: KnownMetricCodes.impression,
            isRate: false,
            color: "#6FBEA2",
            showInLegend: true,
            showInTable: false,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.impressionViewRate,
            isRate: true,
            color: "#6FBEA2",
            showInLegend: false,
            showInTable: true,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.view,
            isRate: false,
            color: "#324472",
            showInLegend: true,
            showInTable: true,
            isDefault: true,
            rateMetric: KnownMetricCodes.impressionViewRate
        },
        {
            metric: KnownMetricCodes.impressionClickRate,
            isRate: true,
            color: "#324472",
            showInLegend: false,
            showInTable: true,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.click,
            isRate: false,
            color: "#59BAC7",
            rateMetric: KnownMetricCodes.impressionClickRate,
            showInLegend: true,
            showInTable: false,
            isDefault: false
        }
    ],
    CPC: [
        {
            metric: KnownMetricCodes.impression,
            isRate: false,
            color: "#6FBEA2",
            showInLegend: true,
            showInTable: false,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.impressionClickRate,
            isRate: true,
            color: "#6FBEA2",
            showInLegend: false,
            showInTable: true,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.click,
            isRate: false,
            color: "#324472",
            showInLegend: true,
            showInTable: true,
            rateMetric: KnownMetricCodes.impressionClickRate,
            isDefault: true
        }
    ],
    CPL: [
        {
            metric: KnownMetricCodes.impression,
            isRate: false,
            color: "#6FBEA2",
            showInLegend: true,
            showInTable: false,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.impressionClickRate,
            isRate: true,
            color: "#6FBEA2",
            showInLegend: false,
            showInTable: true,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.click,
            isRate: false,
            color: "#324472",
            showInLegend: true,
            showInTable: false,
            rateMetric: KnownMetricCodes.impressionClickRate,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.clickLandingRate,
            isRate: true,
            color: "#324472",
            showInLegend: false,
            showInTable: true,
            isDefault: false
        },
        {
            metric: KnownMetricCodes.landing,
            isRate: false,
            color: "#F5850E",
            showInLegend: true,
            showInTable: true,
            rateMetric: KnownMetricCodes.clickLandingRate,
            isDefault: true
        }
    ]
};

export const AdInteractionMetrics: MetricInfo[] = [
    {
        metric: KnownMetricCodes.impression,
        isRate: false,
        color: "#6FBEA2",
        showInLegend: true,
        showInTable: true,
        isDefault: true
    },
    {
        metric: KnownMetricCodes.interaction,
        isRate: false,
        color: "#6FBEA2",
        showInLegend: true,
        showInTable: true,
        isDefault: true
    }
];

interface MetricLabel {
    label: string;
    /** Title used in metric switcher usually at top right corner */
    metricSwitcher?: string;
    /** Header of the metric in the metrics table. */
    tableHeader?: string;
}

const DefaultMetricLabels: { [metric: string]: MetricLabel } = {
    [KnownMetricCodes.impression]: {
        label: "Impressions"
    },
    [KnownMetricCodes.impressionClickRate]: {
        label: "Click rate"
    },
    [KnownMetricCodes.click]: {
        label: "Clicks"
    },
    [KnownMetricCodes.impressionViewRate]: {
        label: "View rate"
    },
    [KnownMetricCodes.view]: {
        label: "Views"
    },
    [KnownMetricCodes.clickLandingRate]: {
        label: "Landing rate"
    },
    [KnownMetricCodes.landing]: {
        label: "Landing"
    },
    [KnownMetricCodes.interaction]: {
        label: "Interactions"
    }
};

export const MetricLabels = {
    metricSwitcher(metricInfo: MetricInfo): string {
        return DefaultMetricLabels[metricInfo.metric].metricSwitcher || DefaultMetricLabels[metricInfo.metric].label;
    },

    tableColumnHeader(metricInfo: MetricInfo): string {
        return DefaultMetricLabels[metricInfo.metric].tableHeader || DefaultMetricLabels[metricInfo.metric].label;
    },

    relativeValueTableColumnHeader(metricInfo: MetricInfo): string {
        return `Share`;
    },

    overDaysOfWeekHeader(metricInfo: MetricInfo): string {
        return `${DefaultMetricLabels[metricInfo.metric].label} over days of week`;
    }
};
